import OrganizationAPI from 'dashboard/api/organization';
import {
  readCrmCache,
  shouldBypassCrmCache,
  writeCrmCache,
} from 'dashboard/helper/crmCache';

const CACHE_TTL = 60 * 1000;

const ORG_SET_STRUCTURE = 'ORG_SET_STRUCTURE';
const ORG_SET_ORPHANS = 'ORG_SET_ORPHANS';
const ORG_SET_COUNTS = 'ORG_SET_COUNTS';
const ORG_SET_QR_CODE = 'ORG_SET_QR_CODE';
const ORG_SET_UI_FLAGS = 'ORG_SET_UI_FLAGS';
const ORG_SET_ACCOUNT = 'ORG_SET_ACCOUNT';

const state = {
  account: {
    name: '',
    description: '',
    global_inbox_id: null,
    funnel_id: null,
    manager_as_admin: false,
  },
  structure: [],
  orphans: [],
  counts: { total: 0, managers: 0, supervisors: 0, consultants: 0 },
  qrCode: null,
  uiFlags: { isFetching: false, isInviting: false, isSaving: false },
};

const getters = {
  getOrgAccount: s => s.account,
  getOrgStructure: s => s.structure,
  getOrgOrphans: s => s.orphans,
  getOrgCounts: s => s.counts,
  getQrCode: s => s.qrCode,
  getOrgUiFlags: s => s.uiFlags,
};

const mutations = {
  [ORG_SET_ACCOUNT](s, account) {
    s.account = account;
  },
  [ORG_SET_STRUCTURE](s, structure) {
    s.structure = structure;
  },
  [ORG_SET_ORPHANS](s, orphans) {
    s.orphans = orphans;
  },
  [ORG_SET_COUNTS](s, counts) {
    s.counts = counts;
  },
  [ORG_SET_QR_CODE](s, qrCode) {
    s.qrCode = qrCode;
  },
  [ORG_SET_UI_FLAGS](s, flags) {
    s.uiFlags = { ...s.uiFlags, ...flags };
  },
};

const actions = {
  async fetchStructure({ commit }, params = {}) {
    const cached = shouldBypassCrmCache(params)
      ? null
      : readCrmCache('organization.structure', {}, CACHE_TTL);

    if (cached) {
      commit(ORG_SET_STRUCTURE, cached.structure ?? []);
      commit(ORG_SET_ORPHANS, cached.orphans ?? []);
      commit(ORG_SET_COUNTS, cached.counts ?? {});
      if (cached.account) commit(ORG_SET_ACCOUNT, cached.account);
      return;
    }

    commit(ORG_SET_UI_FLAGS, { isFetching: true });
    try {
      const { data } = await OrganizationAPI.getAll();
      commit(ORG_SET_STRUCTURE, data.structure ?? []);
      commit(ORG_SET_ORPHANS, data.orphans ?? []);
      commit(ORG_SET_COUNTS, data.counts ?? {});
      if (data.account) commit(ORG_SET_ACCOUNT, data.account);
      writeCrmCache('organization.structure', {}, data);
    } catch {
      // silently fail — UI shows empty state
    } finally {
      commit(ORG_SET_UI_FLAGS, { isFetching: false });
    }
  },

  async saveSettings({ commit }, formData) {
    commit(ORG_SET_UI_FLAGS, { isSaving: true });
    try {
      await OrganizationAPI.saveSettings(formData);
      // Atualiza o estado local com os valores salvos
      commit(ORG_SET_ACCOUNT, {
        name: formData.name,
        description: formData.description,
        global_inbox_id: formData.inboxId || formData.global_inbox_id,
        funnel_id: formData.funnelId || formData.funnel_id,
        manager_as_admin: formData.managerAsAdmin,
      });
      writeCrmCache('organization.structure', {}, null);
    } finally {
      commit(ORG_SET_UI_FLAGS, { isSaving: false });
    }
  },

  async addMember({ dispatch }, data) {
    await OrganizationAPI.addMember(data);
    await dispatch('fetchStructure', { force: true });
    dispatch('agents/get', null, { root: true });
    dispatch('teams/get', null, { root: true });
  },

  async inviteMember({ commit, dispatch }, data) {
    commit(ORG_SET_UI_FLAGS, { isInviting: true });
    try {
      const { data: result } = await OrganizationAPI.inviteMember(data);
      dispatch('fetchStructure', { force: true });
      return result;
    } finally {
      commit(ORG_SET_UI_FLAGS, { isInviting: false });
    }
  },

  async whatsappRequestPairing({ rootGetters }, { userId, phoneNumber }) {
    // Pega o ID da conta atual
    const accountId = rootGetters.getCurrentAccountId;
    
    try {
      const response = await axios.post(`/api/v1/accounts/${accountId}/organization/whatsapp_request_pairing`, {
        user_id: userId,
        phone_number: phoneNumber
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Erro na requisição ao gerar código.');
    }
  },

  async fetchQrCode({ commit }, userId = null) {
    const { data } = await OrganizationAPI.getWhatsappQr(userId);
    commit(ORG_SET_QR_CODE, data.qr_code ?? null);
    return data;
  },

  async whatsappConnect({ commit }, { reset = false } = {}) {
    commit(ORG_SET_QR_CODE, null);
    const { data } = await OrganizationAPI.whatsappConnect({ reset });
    return data;
  },

  async removeMember({ dispatch }, memberId) {
    await OrganizationAPI.removeMember(memberId);
    dispatch('fetchStructure', { force: true });
  },

  async moveConsultant({ dispatch }, { userId, teamId }) {
    await OrganizationAPI.moveConsultant(userId, teamId);
    await dispatch('fetchStructure', { force: true });
  },
};

export default { namespaced: true, state, getters, mutations, actions };
